import { useEffect, useRef, useState } from "react";
import {
  COURSES, TOPICS, LEVELS, filterCourses, createScorm12API,
  getProgress, allProgress, completedCount, type Course, type Progress,
} from "../lib/courses";

export function Learn({ onRequestCourse }: { onRequestCourse?: () => void }) {
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Course | null>(null);
  const [tick, setTick] = useState(0); // re-read progress after a session

  const shown = filterCourses(COURSES, { topic, level, q });
  const prog = allProgress();
  const live = COURSES.filter((c) => c.launch).length;

  if (open) return <Player course={open} onBack={() => { setOpen(null); setTick((t) => t + 1); }} />;

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Learn — master the craft, then teach your team</div>
        <p>
          Beautifully hosted courses on Section 8 investing, underwriting, compliance, negotiation and capital —
          <b> for you, your team, and your community</b>. Track your progress, pick up where you left off, and tell us
          what to build next: the community votes on the courses and features it wants (in <b>Community → Requests</b>).
        </p>
      </div>

      <div className="card pad" style={{ marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input className="bb-name" style={{ flex: 1, minWidth: 200 }} placeholder="Search courses…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="bb-name" value={topic} onChange={(e) => setTopic(e.target.value)}><option value="">All topics</option>{TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <select className="bb-name" value={level} onChange={(e) => setLevel(e.target.value)}><option value="">All levels</option>{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select>
        <span className="tag t-pass" key={tick}>{completedCount()} completed</span>
      </div>

      {shown.length === 0 ? (
        <div className="note">No courses match — clear the filters, or request this topic in Community → Requests.</div>
      ) : (
        <div className="lms-grid">
          {shown.map((c) => <CourseCard key={c.id} c={c} p={prog[c.id]} onOpen={() => setOpen(c)} onRequest={onRequestCourse} />)}
        </div>
      )}
    </div>
  );
}

function CourseCard({ c, p, onOpen, onRequest }: { c: Course; p?: Progress; onOpen: () => void; onRequest?: () => void }) {
  const live = !!c.launch;
  const done = p?.status === "completed";
  return (
    <div className={"card lms-card" + (live ? "" : " soon")}>
      <div className="lms-cover">{c.emoji}</div>
      <div className="lms-body">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          <span className="tag t-watch">{c.topic}</span>
          <span className="tag t-watch" style={{ opacity: .8 }}>{c.level}</span>
          <span className="tag t-watch" style={{ opacity: .8 }}>{c.minutes} min</span>
        </div>
        <div className="lms-title">{c.title}</div>
        <p className="lms-desc">{c.description}</p>
        <div className="sp" style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          {live ? (
            <button className={"btn btn-sm " + (done ? "btn-g" : "btn-p")} onClick={onOpen}>
              {done ? "↻ Review" : p?.status === "in-progress" ? "▸ Resume" : "▸ Start course"}
            </button>
          ) : (
            <button className="btn btn-a btn-sm" onClick={onRequest}>▲ Vote for this course</button>
          )}
          {done && <span className="tag t-pass">✓ Completed{p?.score != null ? ` · ${p.score}%` : ""}</span>}
          {!done && p?.status === "in-progress" && <span className="tag t-watch">In progress</span>}
        </div>
        {!live && <div className="note" style={{ marginTop: 8 }}>Want this next? Vote for it in <b>Community → Requests</b>.</div>}
      </div>
    </div>
  );
}

// --- the SCORM player -------------------------------------------------------
function Player({ course, onBack }: { course: Course; onBack: () => void }) {
  const [ready, setReady] = useState(false);
  const [p, setP] = useState<Progress | undefined>(getProgress(course.id));
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // The course (in the iframe) walks window.parent for `API`; put it there first.
    (window as unknown as { API?: unknown }).API = createScorm12API(course.id, (pr) => setP(pr));
    setReady(true);
    return () => { try { delete (window as unknown as { API?: unknown }).API; } catch { /* */ } };
  }, [course.id]);

  const done = p?.status === "completed";
  return (
    <div className="wrap" style={{ maxWidth: 1280 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <button className="btn btn-g btn-sm" onClick={onBack}>← All courses</button>
        <h2 className="sechead" style={{ margin: 0, fontSize: 20 }}>{course.emoji} {course.title}</h2>
        <div className="sp" style={{ flex: 1 }} />
        {done ? <span className="tag t-pass">✓ Completed{p?.score != null ? ` · ${p.score}%` : ""}</span>
              : <span className="tag t-watch">{p?.status === "in-progress" ? "In progress — your place is saved" : "Not started"}</span>}
        <button className="btn btn-g btn-sm" onClick={() => frameRef.current?.requestFullscreen?.()}>⛶ Fullscreen</button>
      </div>
      <div className="lms-player">
        {ready && <iframe ref={frameRef} title={course.title} src={course.launch} allowFullScreen
          style={{ width: "100%", height: "100%", border: "none", display: "block", background: "#fff" }} />}
      </div>
      <div className="note" style={{ marginTop: 8 }}>
        Your progress and place are saved automatically as you go. Course by Beacon Academy · powered by your SCORM library.
      </div>
    </div>
  );
}
